import { useEffect, useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { assignHeat, fetchHeatCatalog, type HeatCatalog, type HeatOption, type Readiness } from '../data/myApi'
import { bumpReadinessRefresh } from '../data/getReady'
import { Button } from '../components/ui'
import { IconBack } from '../lib/icons'

/*
 * HEAT SELECTION — canon rebuild per heat-selection_desktop.jpg
 * (CMT-2026-08-09-043, audit D15–D19).
 *
 *   D15  top row: back link "‹ {eventName}" + right-aligned mono event date
 *   D16  mono uppercase ticket-type kicker ("INDIVIDUAL — MALE") + H1
 *        "Choose your heat" + verbatim subtitle
 *   D17  teal "{open} of {total} heats still open" derived live from payload
 *   D18  card rows: bold START TIME (the primary, distinguishing label) +
 *        "{left} OF {cap} LEFT" / "FULL" availability pill · FULL rows greyed +
 *        non-selectable. (The "Wave {n} · {x} people" meta was REMOVED per the
 *        CMT-2026-08-11-081455 live-test fix — every heat repeated the same
 *        "Wave 1" and the capacity was already in the pill, so it made the rows
 *        indistinguishable; the TIME alone separates them.)
 *   D19  click = SELECT only (never POSTs); sticky amber guard until a
 *        selection exists; footer Back (ghost) + Confirm heat (filled teal,
 *        disabled until selected) performs the POST. The off-canon
 *        "Assign me one automatically" button is REMOVED (the server
 *        assignHeat(…, null) capability stays dormant).
 *
 * Heat change is FREE: the server excludes the caller's own assignment from
 * fullness counts and re-assignment carries no charge; the current heat is
 * preselected from context.assignedHeatId. Where the data source has not yet
 * supplied a value (startTime/wave/capacity NULL on the wire), the row renders
 * only what exists — nothing is fabricated (PROCESS-3).
 */

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Canon "Sun 16 Aug 2026". UTC-based — the wire has no event-local timezone
 *  yet (named gap); UTC keeps the render deterministic rather than
 *  viewer-timezone-dependent. */
export function formatEventDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${DAY[d.getUTCDay()]} ${d.getUTCDate()} ${MON[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

function isFull(h: HeatOption): boolean {
  return h.spotsRemaining != null && h.spotsRemaining <= 0
}

const monoPill: CSSProperties = {
  borderRadius: 6,
  padding: '4px 9px',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '.07em',
  whiteSpace: 'nowrap',
  flex: 'none',
}

function HeatRow({ heat, selected, onSelect }: {
  heat: HeatOption
  selected: boolean
  onSelect: () => void
}) {
  const full = isFull(heat)
  // The TIME is the primary, distinguishing label (CMT-2026-08-11-081455,
  // Duncan live test). The old meta line "Wave {n} · {x} people" was dropped:
  // every heat carried the SAME "Wave 1" (a meaningless repeated ordinal) and a
  // capacity already shown by the availability pill, so the rows were
  // indistinguishable. Availability now lives entirely in the FULL / "{left} OF
  // {cap} LEFT" pill on the right. When the wire has no start_time (genuinely
  // not sourced), a neutral placeholder renders — never a fabricated time.
  const timeLabel = heat.startTime || heat.label || 'Start time to be confirmed'

  return (
    <button
      onClick={full ? undefined : onSelect}
      disabled={full}
      aria-pressed={selected}
      aria-disabled={full || undefined}
      style={{
        all: 'unset',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        width: '100%',
        padding: '13px 16px',
        background: selected ? 'var(--tint-teal)' : 'var(--surface)',
        border: selected ? '1px solid var(--accent)' : '1px solid var(--hairline-strong)',
        borderRadius: 'var(--r-card)',
        cursor: full ? 'default' : 'pointer',
        opacity: full ? 0.45 : 1,
      }}
    >
      <span className="col" style={{ gap: 3, minWidth: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>{timeLabel}</span>
      </span>
      {full ? (
        <span className="mono" style={{ ...monoPill, border: '1px solid var(--hairline-strong)', color: 'var(--text-faint)' }}>
          FULL
        </span>
      ) : heat.spotsRemaining != null && heat.capacity != null ? (
        <span className="mono" style={{ ...monoPill, border: '1px solid var(--accent)', color: 'var(--accent-soft)' }}>
          {heat.spotsRemaining} OF {heat.capacity} LEFT
        </span>
      ) : null}
    </button>
  )
}

export function HeatSelect({
  eventName,
  eventId,
  idToken,
  base,
  ticketId,
  onBack,
  onAssigned,
}: {
  eventName: string
  eventId: string
  idToken: string
  base: string
  ticketId: string
  onBack: () => void
  onAssigned: (readiness: Readiness) => void
}) {
  const [catalog, setCatalog] = useState<HeatCatalog | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchHeatCatalog(base, eventId, { idToken, ticketId })
      .then((c) => {
        if (cancelled) return
        setCatalog(c)
        // FREE re-selection: a completed heat step reopens preselected on the
        // current heat; choosing another available heat carries no charge.
        if (c.context?.assignedHeatId) setSelected(c.context.assignedHeatId)
      })
      .catch(() => { if (!cancelled) setLoadError(true) })
    return () => { cancelled = true }
  }, [base, eventId, idToken, ticketId])

  const heats = catalog?.heats ?? null
  const context = catalog?.context ?? null
  const typeName = context?.ticketTypeName ?? null
  const eventDate = context?.eventStartsAt ? formatEventDate(context.eventStartsAt) : null

  const openCount = useMemo(() => (heats ?? []).filter((h) => !isFull(h)).length, [heats])

  const confirm = async () => {
    if (!selected) return
    setStatus('submitting')
    setError(null)
    try {
      const readiness = await assignHeat(idToken, base, ticketId, selected)
      onAssigned(readiness)
      // Signal the Get-ready home to re-read server readiness immediately, so the
      // heat step flips to done without a manual refresh (CMT-121325 stale-state;
      // parity with the profile-save + waiver-sign bumps).
      bumpReadinessRefresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'heat assignment failed')
      setStatus('error')
    }
  }

  return (
    <div className="col" style={{ gap: 16 }}>
      {/* D15 — back "‹ {eventName}" left, mono event date right */}
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <button onClick={onBack} className="row gap-6" style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, padding: 0 }}>
          <IconBack size={16} /> {eventName}
        </button>
        {eventDate && (
          <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
            {eventDate}
          </span>
        )}
      </div>

      {/* D16 — type kicker + H1 + verbatim subtitle */}
      <div>
        {typeName && (
          <span className="mono" style={{ display: 'block', fontSize: 10.5, letterSpacing: '.08em', color: 'var(--text-faint)' }}>
            {typeName.toUpperCase()}
          </span>
        )}
        <h1 style={{ fontSize: 22, margin: '4px 0 2px' }}>Choose your heat</h1>
        <p className="secondary" style={{ fontSize: 13, margin: 0 }}>
          {typeName
            ? `Choose a start time for ${typeName}. Heats fill as people choose, so the list is live.`
            : 'Choose a start time. Heats fill as people choose, so the list is live.'}
        </p>
      </div>

      {heats === null && !loadError && (
        <p className="secondary" role="status" style={{ fontSize: 13 }}>Loading available heats…</p>
      )}
      {loadError && (
        <p role="alert" className="secondary" style={{ fontSize: 13 }}>
          Couldn&rsquo;t load the heat list right now — try again shortly.
        </p>
      )}
      {heats !== null && heats.length === 0 && (
        <div role="status" data-wired="false" style={{ border: '1px dashed var(--text-faint)', borderRadius: 'var(--r-card)', padding: 20, color: 'var(--text-secondary)', fontSize: 13 }}>
          {eventName} hasn&rsquo;t published a heat list yet — nothing to choose from.
          Nothing here is fabricated; this screen works the moment heats are set.
        </div>
      )}

      {heats !== null && heats.length > 0 && (
        <>
          {/* D17 — live availability summary */}
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--accent-soft)' }}>
            {openCount} of {heats.length} heats still open
          </span>

          {/* D18 — card rows; D19 — click selects only, never POSTs */}
          <div className="col" style={{ gap: 10 }}>
            {heats.map((h) => (
              <HeatRow
                key={h.heatId}
                heat={h}
                selected={selected === h.heatId}
                onSelect={() => setSelected(h.heatId)}
              />
            ))}
          </div>

          {/* D19 — sticky amber guard until a selection exists */}
          {selected === null && (
            <div
              role="status"
              style={{
                position: 'sticky',
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 14px',
                background: 'var(--warn-bg)',
                color: 'var(--warn)',
                borderRadius: 8,
                fontSize: 12.5,
                fontWeight: 600,
              }}
            >
              ⚠ Choose an available heat to continue
            </div>
          )}

          {/* D19 — footer: Back (ghost) + Confirm heat (filled teal, gated) */}
          <div className="row" style={{ gap: 10 }}>
            <Button variant="ghost" onClick={onBack}>Back</Button>
            <div style={{ flex: 1 }}>
              <Button full disabled={selected === null || status === 'submitting'} onClick={() => { void confirm() }}>
                Confirm heat
              </Button>
            </div>
          </div>
        </>
      )}
      {error && <span role="alert" style={{ fontSize: 12, color: 'var(--warn)' }}>{error}</span>}
    </div>
  )
}
